import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Collapse,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CampaignIcon from "@mui/icons-material/Campaign";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import ArchiveIcon from "@mui/icons-material/Archive";
import PushPinIcon from "@mui/icons-material/PushPin";
import ScheduleIcon from "@mui/icons-material/Schedule";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import PreviewIcon from "@mui/icons-material/Preview";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import announcementsApiService, {
  AnnouncementDTO,
  AnnouncementScheduleDTO,
} from "../../services/announcements-api";
import { formatDate } from "../../services/dateTimeService";
interface AnnouncementsCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface AnnouncementsCardRef {
  openAddDialog: () => void;
}

const AnnouncementsCard = forwardRef<
  AnnouncementsCardRef,
  AnnouncementsCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState<AnnouncementDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<
    number | null
  >(null);
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [formData, setFormData] = useState<AnnouncementDTO>({
    title: "",
    content: "",
    imageUrl: "",
    startDate: "",
    endDate: "",
    priority: "NORMAL",
    category: "",
    status: "DRAFT",
    isPinned: false,
    schedules: [],
  });

  useEffect(() => {
    if (shouldLoad) {
      loadAnnouncements();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  // Filter announcements by status
  const filteredAnnouncements = useMemo(() => {
    if (filterStatus === "ALL") return announcements;
    return announcements.filter((a) => a.status === filterStatus);
  }, [announcements, filterStatus]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await announcementsApiService.getAllAnnouncements();
      setAnnouncements(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (announcement?: AnnouncementDTO) => {
    if (announcement) {
      setEditingId(announcement.id || null);
      setFormData(announcement);
    } else {
      setEditingId(null);
      setFormData({
        title: "",
        content: "",
        imageUrl: "",
        startDate: "",
        endDate: "",
        priority: "NORMAL",
        category: "",
        status: "DRAFT",
        isPinned: false,
        schedules: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      title: "",
      content: "",
      imageUrl: "",
      startDate: "",
      endDate: "",
      priority: "NORMAL",
      category: "",
      status: "DRAFT",
      isPinned: false,
      schedules: [],
    });
  };

  const handleSaveAnnouncement = async () => {
    if (
      !formData.title ||
      !formData.content ||
      !formData.startDate ||
      !formData.endDate
    ) {
      setError(t("announcements.requiredFields"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await announcementsApiService.updateAnnouncement(editingId, formData);
        setSuccess(t("announcements.updated"));
      } else {
        await announcementsApiService.createAnnouncement(formData);
        setSuccess(t("announcements.created"));
      }

      await loadAnnouncements();
      handleCloseDialog();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorSave"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setAnnouncementToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (announcementToDelete === null) return;

    try {
      setLoading(true);
      setError(null);
      await announcementsApiService.deleteAnnouncement(announcementToDelete);
      setSuccess(t("announcements.deleted"));
      await loadAnnouncements();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorDelete"),
      );
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setAnnouncementToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAnnouncementToDelete(null);
  };

  const handlePublish = async (id: number) => {
    try {
      setLoading(true);
      await announcementsApiService.publishAnnouncement(id);
      setSuccess(t("announcements.published"));
      await loadAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorPublish"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = async (id: number) => {
    try {
      setLoading(true);
      await announcementsApiService.unpublishAnnouncement(id);
      setSuccess(t("announcements.unpublished"));
      await loadAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorUnpublish"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id: number) => {
    try {
      setLoading(true);
      await announcementsApiService.archiveAnnouncement(id);
      setSuccess(t("announcements.archived"));
      await loadAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorArchive"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePin = async (id: number) => {
    try {
      setLoading(true);
      await announcementsApiService.togglePin(id);
      setSuccess(t("announcements.pinToggled"));
      await loadAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("announcements.errorTogglePin"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotificationNow = async (id: number) => {
    try {
      setLoading(true);
      await announcementsApiService.sendNotificationNow(id);
      setSuccess(t("announcements.notificationSent"));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("announcements.errorSendNotification"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "success";
      case "DRAFT":
        return "warning";
      case "ARCHIVED":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    return priority === "IMPORTANT" ? "error" : "default";
  };

  const getScheduleStatusIcon = (status?: string) => {
    switch (status) {
      case "SENT":
        return <CheckCircleIcon fontSize="small" color="success" />;
      case "FAILED":
        return <ErrorIcon fontSize="small" color="error" />;
      default:
        return <PendingIcon fontSize="small" color="warning" />;
    }
  };

  const getScheduleStatusColor = (
    status?: string,
  ): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case "SENT":
        return "success";
      case "FAILED":
        return "error";
      default:
        return "warning";
    }
  };

  // Get user's timezone offset in format like "+09:00" or "-05:00"
  const getUserTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset();
    const sign = offset <= 0 ? "+" : "-";
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const minutes = String(Math.abs(offset) % 60).padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  };

  const handleAddSchedule = () => {
    const newSchedule: AnnouncementScheduleDTO = {
      sendAt: "",
      scheduleType: "INITIAL",
    };
    setFormData({
      ...formData,
      schedules: [...(formData.schedules || []), newSchedule],
    });
  };

  const handleRemoveSchedule = (index: number) => {
    const updatedSchedules = [...(formData.schedules || [])];
    updatedSchedules.splice(index, 1);
    setFormData({ ...formData, schedules: updatedSchedules });
  };

  const handleScheduleChange = (
    index: number,
    field: keyof AnnouncementScheduleDTO,
    value: string,
  ) => {
    const updatedSchedules = [...(formData.schedules || [])];
    if (field === "sendAt") {
      // Combine date/time with timezone offset
      updatedSchedules[index] = {
        ...updatedSchedules[index],
        [field]: value ? `${value}:00${getUserTimezoneOffset()}` : "",
      };
    } else {
      updatedSchedules[index] = { ...updatedSchedules[index], [field]: value };
    }
    setFormData({ ...formData, schedules: updatedSchedules });
  };

  // Parse datetime value to local datetime-local format for input
  // Backend may return Unix timestamp (seconds) or ISO string
  const parseScheduleDateTime = (value?: string | number): string => {
    if (value === undefined || value === null || value === "") return "";
    try {
      let date: Date;
      if (typeof value === "number") {
        // Unix timestamp in seconds - convert to milliseconds
        date = new Date(value * 1000);
      } else if (typeof value === "string" && /^\d+(\.\d+)?$/.test(value)) {
        // String that looks like a Unix timestamp
        date = new Date(parseFloat(value) * 1000);
      } else {
        // ISO string or other date string
        date = new Date(value);
      }
      // Check for invalid date
      if (isNaN(date.getTime())) return "";
      // Format as YYYY-MM-DDTHH:mm for datetime-local input using LOCAL time (not UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  return (
    <Card sx={{ boxShadow: hideHeader ? "none" : undefined }}>
      <CardContent sx={{ p: hideHeader ? 0 : undefined }}>
        {!hideHeader && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CampaignIcon /> {t("announcements.title")}
            </Typography>
            <Tooltip title={t("announcements.addNew")}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                variant="contained"
                size="small"
              >
                {t("common.add")}
              </Button>
            </Tooltip>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Status Filter */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 1, mb: 3 }}
        >
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>{t("announcements.status")}</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label={t("announcements.status")}
            >
              <MenuItem value="ALL">{t("common.all")}</MenuItem>
              <MenuItem value="DRAFT">
                {t("announcements.statusDraft")}
              </MenuItem>
              <MenuItem value="PUBLISHED">
                {t("announcements.statusPublished")}
              </MenuItem>
              <MenuItem value="ARCHIVED">
                {t("announcements.statusArchived")}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {loading && !openDialog ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("announcements.titleField")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("announcements.dateRange")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("announcements.priority")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("announcements.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("announcements.pinned")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAnnouncements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t("announcements.noAnnouncements")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAnnouncements.map((announcement) => (
                    <TableRow key={announcement.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {announcement.title.substring(0, 40)}
                          {announcement.title.length > 40 ? "..." : ""}
                        </Typography>
                        {announcement.category && (
                          <Typography variant="caption" color="text.secondary">
                            {announcement.category}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(announcement.startDate)} -{" "}
                          {formatDate(announcement.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={announcement.priority}
                          color={getPriorityColor(announcement.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={announcement.status}
                          color={getStatusColor(announcement.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {announcement.isPinned && (
                          <PushPinIcon color="primary" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          {announcement.status === "DRAFT" && (
                            <Tooltip title={t("announcements.publish")}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handlePublish(announcement.id!)}
                              >
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {announcement.status === "PUBLISHED" && (
                            <>
                              <Tooltip title={t("announcements.unpublish")}>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    handleUnpublish(announcement.id!)
                                  }
                                >
                                  <UnpublishedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("announcements.archive")}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleArchive(announcement.id!)
                                  }
                                >
                                  <ArchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip
                            title={t("announcements.sendNotificationNow")}
                          >
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() =>
                                handleSendNotificationNow(announcement.id!)
                              }
                            >
                              <NotificationsActiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("announcements.togglePin")}>
                            <IconButton
                              size="small"
                              color={
                                announcement.isPinned ? "primary" : "default"
                              }
                              onClick={() => handleTogglePin(announcement.id!)}
                            >
                              <PushPinIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.edit")}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(announcement)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteClick(announcement.id!)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Add/Edit Dialog - continued in next section */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingId ? t("announcements.edit") : t("announcements.add")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label={t("announcements.titleField")}
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              fullWidth
              required
              size="small"
            />
            <Box sx={{ position: "relative" }}>
              <TextField
                label={t("announcements.content")}
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                fullWidth
                required
                multiline
                rows={8}
                size="small"
              />
              <Tooltip title={t("announcements.preview")}>
                <IconButton
                  size="small"
                  onClick={() => setPreviewOpen(true)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                    boxShadow: 1,
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              label={t("announcements.imageUrl")}
              value={formData.imageUrl || ""}
              onChange={(e) =>
                setFormData({ ...formData, imageUrl: e.target.value })
              }
              fullWidth
              size="small"
              placeholder="https://..."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("announcements.startDate")}
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                fullWidth
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t("announcements.endDate")}
                type="date"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                fullWidth
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl fullWidth size="small">
                <InputLabel>{t("announcements.priority")}</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: e.target.value as "NORMAL" | "IMPORTANT",
                    })
                  }
                  label={t("announcements.priority")}
                >
                  <MenuItem value="NORMAL">
                    {t("announcements.priorityNormal")}
                  </MenuItem>
                  <MenuItem value="IMPORTANT">
                    {t("announcements.priorityImportant")}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t("announcements.category")}
                value={formData.category || ""}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                fullWidth
                size="small"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  borderColor: formData.isPinned ? "primary.main" : "divider",
                  bgcolor: formData.isPinned ? "primary.main" : "transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                  minWidth: 100,
                }}
                onClick={() =>
                  setFormData({ ...formData, isPinned: !formData.isPinned })
                }
              >
                <PushPinIcon
                  fontSize="small"
                  color={formData.isPinned ? "info" : "primary"}
                />
                <Typography
                  variant="body2"
                  color={
                    formData.isPinned
                      ? "primary.contrastText"
                      : "text.secondary"
                  }
                  sx={{ fontWeight: formData.isPinned ? 600 : 400 }}
                >
                  {t("announcements.pinned")}
                </Typography>
              </Paper>
            </Stack>

            {/* Notification Schedules Section */}
            <Divider sx={{ my: 1 }} />
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <NotificationsIcon fontSize="small" />{" "}
                  {t("announcements.notificationSchedules")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddSchedule}
                  variant="outlined"
                >
                  {t("announcements.addSchedule")}
                </Button>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                {t("announcements.notificationSchedulesHelp")}
              </Typography>

              {(formData.schedules || []).length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <ScheduleIcon
                    sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t("announcements.noSchedules")}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {(formData.schedules || []).map((schedule, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          type="datetime-local"
                          label={t("announcements.scheduleSendAt")}
                          value={parseScheduleDateTime(schedule.sendAt)}
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "sendAt",
                              e.target.value,
                            )
                          }
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 2 }}
                        />
                        <FormControl
                          size="small"
                          sx={{ flex: 1, minWidth: 120 }}
                        >
                          <InputLabel>
                            {t("announcements.scheduleType")}
                          </InputLabel>
                          <Select
                            value={schedule.scheduleType}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "scheduleType",
                                e.target.value,
                              )
                            }
                            label={t("announcements.scheduleType")}
                          >
                            <MenuItem value="INITIAL">
                              {t("announcements.scheduleTypeInitial")}
                            </MenuItem>
                            <MenuItem value="REMINDER">
                              {t("announcements.scheduleTypeReminder")}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {schedule.status && (
                          <Tooltip
                            title={schedule.errorMessage || schedule.status}
                          >
                            <Chip
                              icon={getScheduleStatusIcon(schedule.status)}
                              label={schedule.status}
                              size="small"
                              color={getScheduleStatusColor(schedule.status)}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSchedule(index)}
                          disabled={schedule.status === "SENT"}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {schedule.sentAt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {t("announcements.scheduleSentAt")}:{" "}
                          {new Date(
                            typeof schedule.sentAt === "number"
                              ? schedule.sentAt * 1000
                              : parseFloat(String(schedule.sentAt)) * 1000,
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveAnnouncement}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>{t("announcements.deleteConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VisibilityIcon /> {t("announcements.preview")}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: 1,
              borderColor:
                formData.priority === "IMPORTANT" ? "info.light" : "divider",
              background: (theme) =>
                formData.priority === "IMPORTANT"
                  ? theme.palette.mode === "dark"
                    ? "rgba(33, 150, 243, 0.15)"
                    : "rgba(33, 150, 243, 0.08)"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.01)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                cursor: "pointer",
              }}
              onClick={() => setPreviewExpanded(!previewExpanded)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  pt: 0.5,
                }}
              >
                {formData.isPinned && (
                  <PushPinIcon fontSize="small" color="primary" />
                )}
                {formData.priority === "IMPORTANT" && (
                  <StarIcon fontSize="small" sx={{ color: "info.main" }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color:
                      formData.priority === "IMPORTANT"
                        ? "info.main"
                        : "text.primary",
                  }}
                >
                  {formData.title || t("announcements.titleField")}
                </Typography>
                {formData.category && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Chip
                      label={formData.category}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </Box>
                )}
                <Collapse in={previewExpanded}>
                  <Box
                    sx={{
                      mt: 1,
                      color: "text.secondary",
                      wordBreak: "break-word",
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        mt: 1.5,
                        mb: 0.5,
                        fontWeight: 600,
                        color: "text.primary",
                      },
                      "& h1": { fontSize: "1.25rem" },
                      "& h2": { fontSize: "1.15rem" },
                      "& h3": { fontSize: "1.1rem" },
                      "& h4, & h5, & h6": { fontSize: "1rem" },
                      "& p": { my: 0.5 },
                      "& ul, & ol": { pl: 2, my: 0.5 },
                      "& li": { my: 0.25 },
                      "& code": {
                        backgroundColor: "action.hover",
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: "0.85em",
                      },
                      "& pre": {
                        backgroundColor: "action.hover",
                        p: 1,
                        borderRadius: 1,
                        overflow: "auto",
                      },
                      "& blockquote": {
                        borderLeft: 3,
                        borderColor: "primary.main",
                        pl: 1.5,
                        ml: 0,
                        fontStyle: "italic",
                      },
                      "& a": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                      "& strong": { fontWeight: 600 },
                      "& img": { maxWidth: "100%", borderRadius: 1 },
                    }}
                  >
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {formData.content || ""}
                    </ReactMarkdown>
                  </Box>
                  {formData.imageUrl && (
                    <Box
                      component="img"
                      src={formData.imageUrl}
                      alt={formData.title}
                      sx={{
                        mt: 1.5,
                        maxWidth: "100%",
                        maxHeight: 200,
                        borderRadius: 1,
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Collapse>
              </Box>
              <IconButton size="small" sx={{ mt: -0.5 }}>
                {previewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

AnnouncementsCard.displayName = "AnnouncementsCard";

export default React.memo(AnnouncementsCard);
